import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";

import { Preloader } from "@/components";
import { ChatMessage } from "@/features/chats";
import { useTrainerChats } from "../model/TrainerChatsContext";
import { useTrainerChatOutlet } from "./TrainerChatUserLayout";

import styles from "./TrainerChatMessagesPage.module.scss";

const formatDate = (value: string) => {
  try {
    const date = new Date(value);
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch (error) {
    return value;
  }
};

const isOutgoing = (author: string) =>
  author === "trainer-user" || author === "support-user";

const validateImageFile = (file: File): string | null => {
  const validImageTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
  ];
  
  if (!validImageTypes.includes(file.type)) {
    return "Разрешены только изображения (JPEG, PNG, GIF, WebP)";
  }
  
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    return "Размер файла не должен превышать 10MB";
  }
  
  return null;
};

const renderMessageContent = (content: string) => {
  const imgTagRegex = /<img>([^<]+)<\/img>/g;
  const parts: Array<{ type: "text" | "image"; content: string }> = [];
  let lastIndex = 0;
  let match;

  while ((match = imgTagRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      const textPart = content.substring(lastIndex, match.index);
      if (textPart.trim()) {
        parts.push({ type: "text", content: textPart });
      }
    }
    
    parts.push({ type: "image", content: match[1] });
    lastIndex = imgTagRegex.lastIndex;
  }
  
  if (lastIndex < content.length) {
    const textPart = content.substring(lastIndex);
    if (textPart.trim()) {
      parts.push({ type: "text", content: textPart });
    }
  }
  
  if (parts.length === 0) {
    parts.push({ type: "text", content });
  }
  
  return parts;
};

export const TrainerChatMessagesPage = () => {
  const { userId, profile } = useTrainerChatOutlet();
  const { getMessages, getCachedMessages, sendSupportMessage, isSending } =
    useTrainerChats();
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const cachedMessages = getCachedMessages(userId);
  const messages = cachedMessages ?? [];

  const sortedMessages = [...messages].sort(
    (a, b) => new Date(a.of_date).getTime() - new Date(b.of_date).getTime(),
  );

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const loadMessages = async () => {
      setLoading(true);

      try {
        const result = await getMessages(userId);

        if (result === null) {
          navigate("..", { replace: true });
        } else {
          setTimeout(() => {
            if (messagesEndRef.current) {
              messagesEndRef.current.scrollIntoView({ behavior: "auto" });
            }
          }, 100);
        }
      } finally {
        setLoading(false);
      }
    };

    if (cachedMessages === undefined && !isLoading) {
      loadMessages();
    }
  }, [cachedMessages, getMessages, isLoading, navigate, userId]);

  useEffect(() => {
    if (messagesContainerRef.current && messagesEndRef.current) {
      const container = messagesContainerRef.current;
      const isNearBottom =
        container.scrollHeight - container.scrollTop <=
        container.clientHeight + 100;

      if (isNearBottom || sortedMessages.length === 0) {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 50);
      }
    }
  }, [sortedMessages]);

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const error = validateImageFile(file);
    if (error) {
      alert(error);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setSelectedImage(file);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSendMessage = async () => {
    const trimmed = draft.trim();
    const hasImage = selectedImage !== null;
    
    if (!trimmed && !hasImage) {
      return;
    }

    const messageText = trimmed || undefined;
    const imageFile = selectedImage || undefined;

    setDraft("");
    if (hasImage) {
      handleRemoveImage();
    }
    
    await sendSupportMessage(userId, messageText, imageFile);
    await getMessages(userId, true);
    
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 200);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (
    event,
  ) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleImageClick = (imageUrl: string) => {
    setFullscreenImage(imageUrl);
  };

  const handleCloseFullscreen = () => {
    setFullscreenImage(null);
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && fullscreenImage) {
        handleCloseFullscreen();
      }
    };

    if (fullscreenImage) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      if (fullscreenImage) {
        document.body.style.overflow = "";
      }
    };
  }, [fullscreenImage]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <button
          onClick={() => navigate(`/trainer/clients/${userId}`)}
          className={styles.backButton}
        >
          ←
        </button>
        <div className={styles.userInfo}>
          <div className={styles.avatar}>
            {profile.fullname
              ? profile.fullname.charAt(0).toUpperCase()
              : "?"}
          </div>
          <div className={styles.userMeta}>
            <span className={styles.userName}>{profile.fullname}</span>
            {profile.username && (
              <span className={styles.userUsername}>@{profile.username}</span>
            )}
          </div>
        </div>
      </div>

      {isLoading && (
        <div className={styles.loader}>
          <Preloader />
        </div>
      )}

      <div
        ref={messagesContainerRef}
        className={clsx(styles.messagesContainer, isLoading && styles.loading)}
      >
        {sortedMessages.map((message) => {
          const outgoing = isOutgoing(message.author);
          const contentParts = renderMessageContent(message.content);

          return (
            <div
              key={message.id}
              className={clsx(
                styles.message,
                outgoing ? styles.outgoing : styles.incoming,
              )}
            >
              <div className={styles.messageContent}>
                {contentParts.map((part, index) => {
                  if (part.type === "image") {
                    return (
                      <img
                        key={index}
                        src={part.content}
                        alt="Message attachment"
                        className={styles.messageImage}
                        onClick={() => handleImageClick(part.content)}
                      />
                    );
                  }
                  return (
                    <span key={index} className={styles.messageText}>
                      {part.content}
                    </span>
                  );
                })}
              </div>
              <div className={styles.messageTime}>
                {formatDate(message.of_date)}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <div className={styles.inputContainer}>
        {imagePreview && (
          <div className={styles.imagePreview}>
            <img src={imagePreview} alt="Preview" />
            <button
              type="button"
              onClick={handleRemoveImage}
              className={styles.removeImageButton}
            >
              ×
            </button>
          </div>
        )}
        <div className={styles.inputWrapper}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageSelect}
            accept="image/*"
            style={{ display: "none" }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={styles.attachButton}
            disabled={isSending}
          >
            📎
          </button>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Введите сообщение..."
            className={styles.textInput}
            rows={1}
            disabled={isSending}
          />
          <button
            type="button"
            onClick={handleSendMessage}
            className={styles.sendButton}
            disabled={isSending || (!draft.trim() && !selectedImage)}
          >
            {isSending ? "..." : "→"}
          </button>
        </div>
      </div>

      {fullscreenImage && (
        <div className={styles.fullscreenOverlay} onClick={handleCloseFullscreen}>
          <img
            src={fullscreenImage}
            alt="Fullscreen"
            className={styles.fullscreenImage}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className={styles.closeButton}
            onClick={handleCloseFullscreen}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};









